import React, { useEffect } from "react";
import { getOGimage, getUrl } from "../../../utils/getUrl";
import Head from "next/head";
import { AiOutlineArrowRight } from "react-icons/ai";
import { client } from "../../../lib/sanity.client";
import { PortableText } from "@portabletext/react";
import Image from "next/image";
import Link from "next/link";
import imageUrlBuilder from "@sanity/image-url";
import { FaSquareXTwitter } from "react-icons/fa6";
import { BsLinkedin } from "react-icons/bs";
import { useRouter } from "next/router";
import { capture } from "../../../utils/analytics/capture";

function urlFor(source) {
  return imageUrlBuilder(client).image(source);
}

function Author({ author }) {
  const router = useRouter();

  useEffect(() => {
    if (router != null) {
      capture({
        category: "browse",
        event: "page_view",
        properties: {
          slug: author?.slug.current,
          name: author?.name,
          page_type: "blog_author",
        },
      });
    }
  }, []);
  const ptComponents = {
    marks: {
      internalLink: ({ value, children }) => {
        const { slug = {} } = value;

        const href = `/blog/${slug.current}`;
        return (
          <a href={href} target="_blank" rel="noreferrer">
            {children}
          </a>
        );
      },
      link: ({ value, children }) => {
        const { blank, href } = value;

        return blank ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ) : (
          <a href={href} target="_blank" rel="noreferrer">
            {children}
          </a>
        );

        // <Link href={href}>{children}</Link>
      },
    },
    types: {
      image: ({ value }) => {
        if (!value?.asset?._ref) {
          return null;
        }
        const src = urlFor(value).url();

        return (
          <div className=" relative my-0 h-64 w-full rounded-md  lg:h-96">
            <Image
              alt={value.alt || " "}
              className=" h-full w-full rounded-md object-cover  object-top"
              layout="fill"
              src={src}
              // src={urlFor(value).width(320).height(240).fit("max").auto("format")}
            />
          </div>
        );
      },
    },
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: author?.name,
    url: `${getUrl()}/blog/author/${author?.slug.current}`,
    image: `${getOGimage()}`,
    sameAs: [
      `https://www.linkedin.com/in/collinskimotho/`,
      `https://x.com/${author?.slug.current}`,
    ],
    jobTitle: "Data Analyst",
  };

  const breadSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "Webpage",
          "@id": `${getUrl()}`,
          name: "Zote",
          url: `${getUrl()}`,
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "Blog",
          "@id": `${getUrl()}/blog`,
          name: "Manyatta - Zote's Blog",
          url: `${getUrl()}/blog`,
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@type": "Person",
          "@id": `${getUrl()}/blog/author/${author?.slug.current}`,
          name: author?.name,
          url: `${getUrl()}/blog/author/${author?.slug.current}`,
        },
      },
    ],
  };
  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta charSet="utf-8" />
        <title>{`${author?.name} | Zote`}</title>
        <meta name="description" content={`${author?.description}`} />
        <meta name="og:title" content={`${author?.name} | Zote`} />
        <meta name="og:description" content={`${author?.description}`} />
        <link
          rel="canonical"
          href={`${getUrl()}/blog/author/${author?.slug.current}`}
        />
        {/* <meta
          name="og:image"
          content={`${post?.mainImage ? urlFor(post?.mainImage).url() : ""}`}
        /> */}
        <meta name="og:image" content={`${getOGimage()}`} />
        <meta
          property="og:url"
          content={`${getUrl()}/blog/author/${author?.slug.current}`}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadSchema) }}
        />
      </Head>
      <main className=" flex flex-col space-y-12 bg-white px-5 py-10 lg:space-y-16 lg:p-24">
        <div className="flex flex-col space-y-3  bg-white  lg:space-y-5">
          <h1 className=" select-none text-3xl font-medium text-gray-900 lg:text-4xl lg:font-medium">
            {author?.name}
          </h1>
          <div className=" prose select-none leading-relaxed text-gray-800 lg:w-2/3 lg:text-lg lg:text-gray-900">
            <PortableText value={author?.bio} components={ptComponents} />
          </div>
          {author?.slug.current === "collins-kimotho" ? (
            <div className="  flex flex-row space-x-4">
              <a
                target="_blank"
                rel="nofollow noreferrer"
                href={`https://x.com/` + "@CollinsKimotho"}
              >
                <div className=" flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-gray-200">
                  <FaSquareXTwitter className="h-5 w-5 cursor-pointer text-zote-primary " />
                </div>
              </a>
              <a
                target="_blank"
                rel="nofollow noreferrer"
                href={`https://www.linkedin.com/in/collinskimotho/`}
              >
                <div className=" flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-gray-200">
                  <BsLinkedin className="h-5 w-5 cursor-pointer text-zote-primary " />
                </div>
              </a>
            </div>
          ) : null}
        </div>
        <div className=" flex flex-col space-y-3 lg:space-y-5">
          <h2 className=" select-none text-xl lg:text-3xl">
            More from this author
          </h2>
          <div className=" grid gap-x-12 gap-y-10 px-0 pt-5 lg:grid-cols-3 lg:gap-y-12 lg:px-0 lg:pb-20">
            {author?.posts.map((post) => {
              return (
                <Link
                  key={post._id}
                  href={`/blog/${encodeURIComponent(post?.slug?.current)}`}
                  passHref
                >
                  <a className="  group flex h-96 flex-col rounded-md border border-gray-150 bg-white hover:shadow-xl lg:h-[32rem]">
                    <div className=" relative h-2/5 rounded-t-md bg-gray-200 lg:h-1/2">
                      <Image
                        className=" cursor-pointer rounded-t-md object-cover lg:object-top"
                        src={
                          post?.mainImage ? urlFor(post?.mainImage).url() : ""
                        }
                        layout="fill"
                        alt={post?.title}
                      />
                    </div>
                    <div className=" flex h-1/2 flex-col justify-between space-y-3 rounded-b-md px-6 py-6">
                      <div className=" flex flex-col space-y-2">
                        <h2 className=" line-clamp-3 cursor-pointer select-none text-xl font-semibold">
                          {post?.title}
                        </h2>
                        <span className="  line-clamp-3 select-none text-gray-700">
                          {post?.description}
                        </span>
                      </div>
                      <div className=" flex cursor-pointer flex-row items-center space-x-2 text-zote-primary group-hover:text-gray-900">
                        <span className="  font-medium ">Read now</span>
                        <AiOutlineArrowRight className=" transition duration-300 group-hover:translate-x-1 " />
                      </div>
                    </div>
                  </a>
                </Link>
              );
            })}
          </div>
        </div>
      </main>
    </>
  );
}

export async function getStaticPaths() {
  const paths = await client.fetch(
    `*[_type == "author" && defined(slug.current)][].slug.current`
  );
  // console.log(paths);
  return {
    paths: paths.map((slug) => ({ params: { author: slug } })),
    fallback: true,
  };
}

export const getStaticProps = async (context) => {
  // It's important to default the slug/post so that it doesn't return "undefined"
  const { author = "" } = context.params;

  // console.log(author);

  const auth = await client.fetch(
    `*[_type == "author" && slug.current == $author][0]{
        name,
        bio,
        slug,
        description,
        "authorImage": image.asset->url,
        "posts": *[_type == "post" && author->name == ^.name]{
          title,
          slug,
          _id,
          description,
          "categories": categories[]->{title, description, _id},
          mainImage,
          publishedAt,
          body,
        }
      }`,
    { author }
  );
  if (!auth) {
    return {
      notFound: true,
    };
  }
  return {
    props: {
      author: auth,
    },
    revalidate: 5,
  };
};

export default Author;
